import type { User } from './types';
import { hasAnyPermission } from './permissions';
import { canViewTreasury } from '@/modules/treasury/utils/permissions';

const COMPANY_FULL_ACCESS = 'company.full_access';

export function canViewBookings(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.view_bookings', 'location.edit_bookings'], locationId);
}

export function canManageBookings(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.edit_bookings'], locationId);
}

/**
 * Server-side booking CSV export (PENDINGS #32). Its own codename because
 * the file carries guest names — a personal-data extraction. Seeded to
 * roles that already edit bookings (auth_integration migration 0036); no
 * broader fallback on purpose.
 */
export function canExportBookings(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.export_bookings'], locationId);
}

export function canViewAccommodations(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'location.view_bookings', 'location.edit_bookings', 'location.manage_accommodations'],
    locationId,
  );
}

/**
 * Actividades: tres permisos desde 2026-11-02, no uno.
 *
 * `location.manage_activities` iba detrás de los 7 viewsets del módulo, así
 * que el monitor que marcaba asistencia podía borrar el catálogo. El corte
 * separa la trastienda (`manage_activity_catalog`) del día a día
 * (`operate_activities`), y añade una lectura sin escritura
 * (`view_activities`) que antes no existía.
 *
 * Ver `shaka-api/docs/guides/PERMISSIONS_BACKLOG.md`.
 */
const ACTIVITY_READ_CODENAMES = [
  'location.view_activities',
  'location.manage_activity_catalog',
  'location.operate_activities',
];

/** Abre el módulo: cualquiera de los tres permisos deja ver actividades. */
export function canViewActivities(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, ...ACTIVITY_READ_CODENAMES], locationId);
}

/** Trastienda: categorías, plantillas y reglas de recurrencia. */
export function canManageActivityCatalog(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.manage_activity_catalog'], locationId);
}

/** Día a día: sesiones, asignaciones de personal e inscripciones. */
export function canOperateActivities(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.operate_activities'], locationId);
}

export function canViewGuests(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'location.view_bookings', 'location.edit_bookings', 'location.manage_guests'],
    locationId,
  );
}

export function canManageGuests(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.edit_bookings', 'location.manage_guests'], locationId);
}

export function canManagePOS(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.manage_pos'], locationId);
}

/**
 * Can reach Commerce → Sales: the ledger, the adjustments feed, the reports.
 *
 * Deliberately wider than `canManagePOS`. `location.view_pos_sales` is
 * read-only — a manager reviewing takings or an accountant reconciling should
 * never need the ability to settle, void or refund just to look. Operating the
 * POS implies reading it, so `manage_pos` passes here too (mirroring
 * CanViewOrManagePOSSales server-side, where _can_view ORs both).
 *
 * The actions inside Sales stay gated on `canCancelPOSOrders` — this only
 * opens the door.
 */
export function canViewPOSSales(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'location.view_pos_sales', 'location.manage_pos'],
    locationId,
  );
}

export function canViewStaffModule(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'company.manage_users', 'location.view_staff', 'location.edit_staff'],
    locationId,
  );
}

export function canManageStaffModule(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'company.manage_users', 'company.manage_staff', 'location.edit_staff'],
    locationId,
  );
}

/**
 * Can CREATE a staff record (or staff position) at the given scope.
 *
 * `locationId === null` means company scope, and there the gate is **not**
 * `location.edit_staff`: the backend requires a company-level grant to create
 * a company-scope Staff/StaffPosition (2026-08-03 audit). Offering the action
 * to someone holding only `location.edit_staff` walks them through the whole
 * onboarding wizard and then 403s after the person is already filled in.
 *
 * The company-scope gate is `canManageCompanyStaff`, which also accepts
 * `company.manage_staff` — the Staff-module codename an HR role holds to hire
 * without administering every account in the tenant. Using
 * `canManageCompanyUsers` here instead would hide the action from exactly the
 * roles the backend `StaffPermissionMixin` lets through.
 *
 * Location scope is unchanged — `canManageStaffModule` still governs it.
 */
export function canCreateStaffAtScope(
  user: User | null | undefined,
  locationId?: string | null,
): boolean {
  if (!locationId) return canManageCompanyStaff(user);
  return canManageStaffModule(user, locationId);
}

/**
 * Can settle a staff member's token debt. `settle_staff_debt` is accepted
 * alongside `edit_staff` (back-compat with existing managers) — mirroring the
 * server gate on the `settle-debt` action. Also unlocks the treasury
 * settlement-target reads the settle dialog needs.
 */
export function canSettleStaffDebt(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'company.manage_users', 'location.settle_staff_debt', 'location.edit_staff'],
    locationId,
  );
}

export function canViewShifts(user: User | null | undefined, locationId?: string | null): boolean {
  return canViewStaffModule(user, locationId);
}

export function canManagePayrollModule(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'company.manage_users', 'location.edit_staff'], locationId);
}

export function canManageSuppliers(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.manage_inventory'], locationId);
}

// Bar & Kitchen, Retail/Shop and Suppliers are all inventory-module
// surfaces governed by `location.manage_inventory`. Gates the Commerce
// rail entries that would otherwise leak to any user whose location has
// the inventory module active regardless of their role (e.g. Frontdesk).
export function canManageInventory(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.manage_inventory'], locationId);
}

// READ access to the inventory/shop product catalog. Mirrors the backend
// CanManageInventory/CanManageShop view codenames: inventory managers OR
// guest-facing roles (manage_guests) — so Frontdesk can pick a sellable
// when charging a guest folio without being able to edit the catalog.
export function canReadProductCatalog(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'location.manage_inventory', 'location.manage_guests'],
    locationId,
  );
}

export function canManageCompanyUsers(user: User | null | undefined): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'company.manage_users'], null);
}

/**
 * Hiring at company level. `company.manage_staff` is the Staff-module
 * counterpart of manage_users — it exists so an HR role can create company
 * employees and positions without also administering the tenant's accounts.
 * Mirrors StaffPermissionMixin._require_staff_access on the backend.
 */
export function canManageCompanyStaff(user: User | null | undefined): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'company.manage_users', 'company.manage_staff'],
    null,
  );
}

/**
 * Account administration for a given scope.
 *
 * Company and location are separate scopes and this is the seam between
 * them: `company.manage_users` administers the tenant's people,
 * `location.manage_users` administers one site's. A location Manager
 * holding every `location.*` permission used to be locked out of Users &
 * Roles entirely, because the only gate was the company codename — so
 * granting it meant handing out tenant-wide administration.
 *
 * Pass the locationId of the surface being gated; pass null for the
 * company surface, where only the company codename counts. Mirrors backend
 * `CanManageUsers` + `managed_location_ids`.
 */
export function canManageUsersAtScope(
  user: User | null | undefined,
  locationId?: string | null,
): boolean {
  if (canManageCompanyUsers(user)) return true;
  if (!locationId) return false;
  return hasAnyPermission(user, ['location.manage_users'], locationId);
}

/**
 * Access-role management: company admins always; location users need
 * location.manage_roles for the given location (mirrors backend
 * CanManageAccessRoles).
 */
export function canManageAccessRoles(user: User | null | undefined, locationId?: string | null): boolean {
  if (canManageCompanyUsers(user)) return true;
  if (!locationId) return false;
  return hasAnyPermission(user, ['location.manage_roles'], locationId);
}

export function canManageCompanyLocations(user: User | null | undefined): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'company.manage_locations'], null);
}

/**
 * canManageLocationSettings — configuring ONE camp: the Management
 * Overview and the whole Settings domain (hours, seasons, capacity,
 * monetary and guest-payment defaults, contact, integrations).
 *
 * The location-scope twin of `company.manage_locations`, exactly as
 * `location.manage_users` is the twin for Users & Roles. Until
 * 2026-09-04 both surfaces were gated on the company codename alone, so
 * a Manager holding every `location.*` permission could not open the
 * settings of the site they run — and, worse, the rail still showed them
 * the Management domain (Users & Roles survived the gate) whose hub URL
 * pointed at the one entry they could not open, so the click landed on a
 * 403. See PENDINGS #49(a).
 *
 * Mirrors backend `_require_location_settings_access`. What it does NOT
 * cover is enforced server-side in `LocationViewSet.partial_update`:
 * `activeModules` (a price — Shaka bills guest_days x module rates) and
 * `isActive` (retiring the camp) stay `company.manage_locations`, as do
 * the company's billing profiles.
 */
export function canManageLocationSettings(
  user: User | null | undefined,
  locationId?: string | null,
): boolean {
  if (canManageCompanyLocations(user)) return true;
  if (!locationId) return false;
  return hasAnyPermission(user, ['location.manage_settings'], locationId);
}

export function canManageCompanyIntegrations(user: User | null | undefined): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'company.manage_integrations'], null);
}

export function canManageCompanyData(user: User | null | undefined): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'company.manage_data'], null);
}

/**
 * canViewPlatformInvoices — the invoices Shaka issues TO this company for
 * platform usage, not the company's own billing.
 *
 * `company.view_invoices` covers every location. `location.view_invoices` is
 * granted per location and is checked with a locationId, so a site manager
 * only sees their own bill when the owner explicitly hands it over.
 */
export function canViewPlatformInvoices(
  user: User | null | undefined,
  locationId?: string | null,
): boolean {
  if (hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'company.view_invoices'], null)) return true;
  return hasAnyPermission(user, ['location.view_invoices'], locationId);
}

/**
 * canAccessCompanyScope — true when the user has *any* company-level
 * permission, so they may land on bare /company/* surfaces (overview,
 * persons directory, tokens, settings entry).
 *
 * Each specific company surface still applies its own narrower gate
 * (CompanyModulePageGuard / TreasuryPageGuard); this umbrella check is
 * used by the `CompanyPage` wrapper to keep location-only operators
 * out of the tenant-wide directory pages — bug #5 part 2. Mirrors
 * `company.*` codenames consumed by the corresponding CompanySidebar
 * gates (treasury / payroll perms are included so an operator with
 * only those can still reach the company overview from the rail).
 */
export function canAccessCompanyScope(user: User | null | undefined): boolean {
  return hasAnyPermission(
    user,
    [
      COMPANY_FULL_ACCESS,
      'company.manage_users',
      'company.manage_locations',
      'company.manage_integrations',
      'company.manage_data',
      'company.view_treasury',
    ],
    null,
  );
}

export function canDeleteBookings(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.delete_bookings'], locationId);
}

export function canCancelPOSOrders(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.cancel_pos_orders'], locationId);
}

// Devolver y rectificar dejaron de compartir permiso con cancelar el
// 2026-11-02: cancelar tira una comanda ABIERTA, donde no se ha cobrado
// nada; devolver saca dinero del cajón de una venta ya liquidada y
// rectificar la reescribe. El backend los separa en el mismo sitio
// (`_ORDER_ACTION_PERMISSIONS` en `apps/pos/views.py`).
export function canRefundPOSOrders(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.refund_pos_orders'], locationId);
}

export function canAmendPOSOrders(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.amend_pos_orders'], locationId);
}

// Authorize "on the house" (location_comp) purchases at POS. Enforced
// backend-side against the wristband-scanned authorizer; this helper only
// gates whether the *scanned* user qualifies. Backend codename:
// `location.authorize_pos_comp`.
export function canAuthorizePOSComp(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(user, [COMPANY_FULL_ACCESS, 'location.authorize_pos_comp'], locationId);
}

/**
 * Whether the user is allowed to operate a POS workstation from inside
 * the Deck web app. Surfaces the centered POS launcher in the app
 * header. Backend permission codename `location.use_web_pos` is the
 * canonical key — adding it to the seed catalog is task-side work.
 *
 * Falls back to `location.manage_pos` so existing operators with full
 * POS management keep access to the launcher even before the new
 * codename is granted out — that mirrors how the rail item
 * `Point of Sale` is already gated.
 */
export function canUseWebPOS(user: User | null | undefined, locationId?: string | null): boolean {
  return hasAnyPermission(
    user,
    [COMPANY_FULL_ACCESS, 'location.use_web_pos', 'location.manage_pos'],
    locationId,
  );
}

